import React, { useState, useEffect } from 'react';
import './StudentOutcomesDetails_Program.css';
import { accessibleColors } from '../utils/contrastUtils';
import { getAllTemplates, getAllCourses } from '../services/api';

// Function to get program-specific metrics based on program ID
const getProgramMetrics = (programId) => {
  // Default metrics if program ID doesn't match any case
  let metrics = {
    plosAchieved: {
      value: '87%',
      label: 'students meet'
    },
    topCertification: {
      value: '94%',
      label: 'CFA Level 1'
    },
    employerAlignment: {
      value: '73%',
      label: 'skills match'
    },
    studentSatisfaction: {
      value: '4.5',
      label: '/ 5.0'
    }
  };

  // Program-specific metrics
  switch (programId) {
    case 1: // BSM-1 or Accountancy
      metrics = {
        plosAchieved: {
          value: '92%',
          label: 'students meet'
        },
        topCertification: {
          value: '96%',
          label: 'CPA'
        },
        employerAlignment: {
          value: '85%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.7',
          label: '/ 5.0'
        }
      };
      break;
    case 2: // BSM-2 or Management
      metrics = {
        plosAchieved: {
          value: '87%',
          label: 'students meet'
        },
        topCertification: {
          value: '94%',
          label: 'CFA Level 1'
        },
        employerAlignment: {
          value: '73%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.5',
          label: '/ 5.0'
        }
      };
      break;
    case 3: // BSM-3 or Marketing
      metrics = {
        plosAchieved: {
          value: '79%',
          label: 'students meet'
        },
        topCertification: {
          value: '89%',
          label: 'Digital Marketing'
        },
        employerAlignment: {
          value: '81%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.3',
          label: '/ 5.0'
        }
      };
      break;
    case 4: // Operations Management
      metrics = {
        plosAchieved: {
          value: '84%',
          label: 'students meet'
        },
        topCertification: {
          value: '91%',
          label: 'Six Sigma'
        },
        employerAlignment: {
          value: '88%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.4',
          label: '/ 5.0'
        }
      };
      break;
    case 5: // Supply Chain Management
      metrics = {
        plosAchieved: {
          value: '83%',
          label: 'students meet'
        },
        topCertification: {
          value: '87%',
          label: 'CSCP'
        },
        employerAlignment: {
          value: '91%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.2',
          label: '/ 5.0'
        }
      };
      break;
    case 6: // Entrepreneurship
      metrics = {
        plosAchieved: {
          value: '76%',
          label: 'students meet'
        },
        topCertification: {
          value: '83%',
          label: 'Venture Capital'
        },
        employerAlignment: {
          value: '69%',
          label: 'skills match'
        },
        studentSatisfaction: {
          value: '4.8',
          label: '/ 5.0'
        }
      };
      break;
    default:
      // Use default metrics
      break;
  }

  return metrics;
};

// Function to get program-specific certification data based on program ID
const getProgramCertifications = (programId) => {
  switch (programId) {
    case 1: // BSM-1 or Accountancy
      return [
        { name: 'CPA', passRate: 95 },
        { name: 'ACCA', passRate: 87 },
        { name: 'CMA', passRate: 82 }
      ];
    case 2: // BSM-2 or Management
      return [
        { name: 'CFA', passRate: 94 },
        { name: 'PMP', passRate: 88 },
        { name: 'SHRM', passRate: 76 }
      ];
    case 3: // BSM-3 or Marketing
      return [
        { name: 'Digital', passRate: 89 },
        { name: 'AMA', passRate: 79 },
        { name: 'SEO', passRate: 85 }
      ];
    case 4: // Operations Management
      return [
        { name: 'Six Sigma', passRate: 91 },
        { name: 'CPIM', passRate: 83 },
        { name: 'PMP', passRate: 78 }
      ];
    case 5: // Supply Chain Management
      return [
        { name: 'CSCP', passRate: 87 },
        { name: 'CPSM', passRate: 84 },
        { name: 'CPIM', passRate: 79 }
      ];
    case 6: // Entrepreneurship
      return [
        { name: 'Venture', passRate: 83 },
        { name: 'CBDO', passRate: 71 },
        { name: 'PMI', passRate: 68 }
      ];
    default:
      return [
        { name: 'ACCA', passRate: 95 },
        { name: 'CMA', passRate: 78 },
        { name: 'CPA', passRate: 65 }
      ];
  }
};

// Function to get program-specific learning outcomes achievement data
const getProgramLearningOutcomes = (programId) => {
  switch (programId) {
    case 1: // BSM-1 or Accountancy
      return [
        { achievement: 92 }, // LG1
        { achievement: 85 }, // LG2
        { achievement: 88 }, // LG3
        { achievement: 90 }  // LG4
      ];
    case 2: // BSM-2 or Management
      return [
        { achievement: 87 }, // LG1
        { achievement: 78 }, // LG2
        { achievement: 82 }, // LG3
        { achievement: 84 }  // LG4
      ];
    case 3: // BSM-3 or Marketing
      return [
        { achievement: 79 }, // LG1
        { achievement: 72 }, // LG2
        { achievement: 81 }, // LG3
        { achievement: 76 }  // LG4
      ];
    case 4: // Operations Management
      return [
        { achievement: 89 }, // LG1
        { achievement: 76 }, // LG2
        { achievement: 70 }, // LG3
        { achievement: 84 }  // LG4
      ];
    case 5: // Supply Chain Management
      return [
        { achievement: 91 }, // LG1
        { achievement: 82 }, // LG2
        { achievement: 78 }, // LG3
        { achievement: 83 }  // LG4
      ];
    case 6: // Entrepreneurship
      return [
        { achievement: 76 }, // LG1
        { achievement: 69 }, // LG2
        { achievement: 73 }, // LG3
        { achievement: 82 }  // LG4
      ];
    default:
      return [
        { achievement: 82 }, // LG1
        { achievement: 55 }, // LG2
        { achievement: 42 }, // LG3
        { achievement: 77 }  // LG4
      ];
  }
};

const StudentOutcomesDetails_Program = ({ selectedProgram, onBack, onPLOSelect }) => {
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('2025 Spring');
  const [certification, setCertification] = useState('All');
  const [expandedCards, setExpandedCards] = useState({}); // Track multiple expanded cards
  const [allExpanded, setAllExpanded] = useState(false); // Track if all cards are expanded
  const [school, setSchool] = useState('MARYLAND');
  

  // Get school from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const schoolParam = urlParams.get('school');
    if (schoolParam) {
      setSchool(schoolParam);
    }
  }, []);

  // Get initial program metrics based on selected program ID
  const programMetrics = getProgramMetrics(selectedProgram?.id);
  const programCertifications = getProgramCertifications(selectedProgram?.id);
  const programAchievements = getProgramLearningOutcomes(selectedProgram?.id);
  
  const defaultLearningOutcomes = [
    {
      id: 'LG1',
      name: 'Data-driven Decision Making',
      achievement: programAchievements[0].achievement,
      relatedCourses: ['BS101', 'BS102', 'BS103']
    },
    {
      id: 'LG2',
      name: 'Financial Analysis and Modeling',
      achievement: programAchievements[1].achievement,
      relatedCourses: ['BS101', 'BS102', 'BS103']
    },
    {
      id: 'LG3',
      name: 'Professional Ethics and Responsibility',
      achievement: programAchievements[2].achievement,
      relatedCourses: ['BS101', 'BS102', 'BS103']
    },
    {
      id: 'LG4',
      name: 'Business Administration',
      achievement: programAchievements[3].achievement,
      relatedCourses: ['BS101', 'BS102']
    }
  ];
  
  const [programData, setProgramData] = useState({
    name: selectedProgram?.name || 'BS in Management',
    ...programMetrics,
    learningOutcomes: defaultLearningOutcomes,
    certifications: programCertifications
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get updated mock data for this specific program
        const programMetrics = getProgramMetrics(selectedProgram?.id);
        const programCertifications = getProgramCertifications(selectedProgram?.id);
        const programAchievements = getProgramLearningOutcomes(selectedProgram?.id);
        
        // Use dynamic school parameter for API calls
        const templatesResponse = await getAllTemplates(school);
        const coursesResponse = await getAllCourses(school, selectedProgram?.name);
        
        //console.log("Selected Program:", selectedProgram);
        //console.log("Templates API response:", templatesResponse);
        //console.log("Courses API response:", coursesResponse);
        
        // Process data if both API calls succeed
        if (templatesResponse.templates?.[0]?.LG && coursesResponse.course_list) {
          // Map Learning Goals from templates API
          const learningOutcomes = templatesResponse.templates[0].LG.map((lg, index) => {
            // Get achievement values from program-specific mock data if available
            let achievementValue = index < programAchievements.length 
              ? programAchievements[index].achievement 
              : 75; // Default
            
            return {
              id: lg.title, // Use title as ID (e.g., "LG 1")
              name: lg.description, // Use description as name
              achievement: achievementValue,
              relatedCourses: coursesResponse.course_list // Store all courses, we'll handle display in the render
            };
          });
          
          // Update program data with real data and program-specific mock metrics
          setProgramData(prevData => ({
            ...prevData,
            name: selectedProgram?.name || prevData.name,
            ...programMetrics,
            learningOutcomes,
            certifications: programCertifications
          }));
        } else {
          // If API calls failed, still update with program-specific mock data
          setProgramData(prevData => ({
            ...prevData,
            name: selectedProgram?.name || prevData.name,
            ...programMetrics,
            certifications: programCertifications
          }));
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        
        // Update with program-specific mock data on error
        const programMetrics = getProgramMetrics(selectedProgram?.id);
        const programCertifications = getProgramCertifications(selectedProgram?.id);
        
        setProgramData(prevData => ({
          ...prevData,
          name: selectedProgram?.name || prevData.name,
          ...programMetrics,
          certifications: programCertifications
        }));
        
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedProgram, school]);

  const handleViewDetails = (outcome) => {
    if (onPLOSelect) {
      onPLOSelect(outcome);
    }
  };


  const toggleCard = (outcomeId) => {
    // Update expandedCards object to toggle the specified card
    setExpandedCards(prev => {
      const newState = {
        ...prev,
        [outcomeId]: !prev[outcomeId]
      };
      
      // Update allExpanded state based on whether all cards are now expanded
      const allCardsExpanded = programData.learningOutcomes.every(outcome => 
        newState[outcome.id]
      );
      setAllExpanded(allCardsExpanded);
      
      return newState;
    });
  };

  const toggleAllCards = () => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);
    
    // Create an object with all cards either expanded or collapsed
    const newExpandedCards = {};
    programData.learningOutcomes.forEach(outcome => {
      newExpandedCards[outcome.id] = newExpandedState;
    });
    
    setExpandedCards(newExpandedCards);
  };


  if (loading) {
    return <div className="loading">Loading program outcomes data...</div>;
  }

  return (
    <div className="dashboard student-outcomes-details">
      {/* Header with filters */}
      <div className="filters">
        <div className="filter">
          <img src="/school.svg" alt="School" className="filter-icon" />
          <select 
            className="filter-select"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
          >
            <option value={school}>{school}</option>
          </select>
        </div>
        
        <div className="filter">
          <img src="/page-1-calendar.svg" alt="Calendar" className="filter-icon" />
          <select 
            className="filter-select"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          >
            <option>2025 Spring</option>
          </select>
        </div>

        <div className="filter">
          <img src="/program-icon.svg" alt="Program" className="filter-icon" />
          <select className="filter-select">
            <option>{programData.name}</option>
          </select>
        </div>

        <div className="filter">
          <img src="/certification-icon.svg" alt="Certification" className="filter-icon" />
          <select 
            className="filter-select"
            value={certification}
            onChange={(e) => setCertification(e.target.value)}
          >
            <option>All</option>
            <option>CFA</option>
            <option>CPA</option>
            <option>ACCA</option>
          </select>
        </div>

        <button className="back-button" onClick={onBack}>
          <img src="/back-arrow.svg" alt="Back" />
          Back
        </button>
      </div>

      {/* Program Title and Standards Badge */}
      <div className="program-header">
        <h1>{programData.name} - Students Outcomes</h1>
        <div className="standards-badge">
          <span>Assessed in line with AACSB Standards</span>
        </div>
      </div>

      {/* Key Performance Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <h3>PLOs Achieved</h3>
          <div className="metric-value">{programData.plosAchieved.value}</div>
          <div className="metric-label">{programData.plosAchieved.label}</div>
        </div>

        <div className="metric-card">
          <h3>Top Certification</h3>
          <div className="metric-value">{programData.topCertification.value}</div>
          <div className="metric-label">{programData.topCertification.label}</div>
        </div>

        <div className="metric-card">
          <h3>Employer Alignment</h3>
          <div className="metric-value">{programData.employerAlignment.value}</div>
          <div className="metric-label">{programData.employerAlignment.label}</div>
        </div>

        <div className="metric-card">
          <h3>Student Satisfaction</h3>
          <div className="metric-value">{programData.studentSatisfaction.value}</div>
          <div className="metric-label">{programData.studentSatisfaction.label}</div>
        </div>
      </div>

      {/* Program Learning Outcomes */}
      <div className="outcomes-section">
        <div className="outcomes-header">
          <h2>Program Learning Outcomes (PLOs)</h2>
          <button className="toggle-all-button" onClick={toggleAllCards}>
            {allExpanded ? "Collapse All" : "Expand All"}
          </button>
        </div>
        
        <div className="learning-outcomes-grid">
          {programData.learningOutcomes.map(outcome => {
            const isExpanded = expandedCards[outcome.id];
            
            return (
              <div 
                key={outcome.id} 
                className={`outcome-card ${isExpanded ? 'expanded' : 'collapsed'}`}
                onClick={() => toggleCard(outcome.id)}
              >
                <div className="outcome-header">
                  <div className="outcome-icon">
                    <span className="lg-badge">{outcome.id}</span>
                  </div>
                  <h3>{outcome.name}</h3>
                  <div className="expand-indicator">
                    {isExpanded ? '−' : '+'}
                  </div>
                </div>
                
                <div className="progress-container">
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar" 
                      style={{width: `${outcome.achievement}%`, backgroundColor: getProgressColor(outcome.achievement)}}
                    ></div>
                  </div>
                  <div className="progress-percentage">{outcome.achievement}% Achieved</div>
                </div>

                {/* Conditional rendering based on expanded state */}
                {isExpanded && (
                  <>
                    <div className="related-courses">
                      <div className="related-courses-label">Related Courses</div>
                      <div className="course-tags">
                        {Array.isArray(outcome.relatedCourses) && outcome.relatedCourses.slice(0, 6).map((course, index) => (
                          <span key={index} className="course-tag">
                            {typeof course === 'object' ? course.course_code || course.code || 'Unknown Course' : course}
                          </span>
                        ))}
                        {Array.isArray(outcome.relatedCourses) && outcome.relatedCourses.length > 6 && (
                          <span className="course-tag more-courses">
                            +{outcome.relatedCourses.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="view-details-container">
                      <button 
                        className="view-details-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card toggle when clicking the button
                          handleViewDetails(outcome);
                        }}
                      >
                        View Details <span>&rarr;</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Certification Analytics */}
      {/* <div className="certification-section">
        <div className="certification-header">
          <h2>Certification Analytics</h2>
          <button className="view-more-button">
            View More <span>&rarr;</span>
          </button>
        </div>
        
        <div className="certification-content">
          <h3>Certification Pass Rates</h3>
          
          <div className="certification-rates">
            {programData.certifications.map(cert => (
              <div key={cert.name} className="certification-rate-item">
                <div className="cert-name">{cert.name}</div>
                <div className="cert-progress-container">
                  <div 
                    className="cert-progress-bar" 
                    style={{width: `${cert.passRate}%`}}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div> */}
    </div>
  );
};



// Helper function to determine color based on achievement percentage
const getProgressColor = (percentage) => {
  if (percentage >= 80) {
    return accessibleColors.statusBackgroundColors.exceed; // Green
  } else if (percentage >= 60) {
    return accessibleColors.statusBackgroundColors.meet; // Blue
  } else if (percentage >= 40) {
    return accessibleColors.statusBackgroundColors.atRisk; // Orange
  } else {
    return '#C62828'; // Darker red for better contrast
  }
};

export default StudentOutcomesDetails_Program; 